import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PollForm } from "./PollForm";
import { PollResults } from "./PollResults";

interface Cluster {
  id: string;
  label: string;
  description: string;
  image_url?: string;
  order: number;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description: string;
  order: number;
}

interface PollRendererProps {
  postId: string;
}

export const PollRenderer = ({ postId }: PollRendererProps) => {
  const [pollId, setPollId] = useState<string | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [hideZeroAllocated, setHideZeroAllocated] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [clusterAllocations, setClusterAllocations] = useState<Record<string, number>>({});
  const [subAllocations, setSubAllocations] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const totalPoints = Object.values(clusterAllocations).reduce((sum, val) => sum + val, 0);

  useEffect(() => {
    loadPoll();
  }, [postId]);

  const loadPoll = async () => {
    try {
      const { data: poll } = await supabase
        .from("polls")
        .select("*")
        .eq("blog_post_id", postId)
        .single();

      if (!poll) {
        setLoading(false);
        return;
      }

      setPollId(poll.id);
      const settings = poll.settings as { hideZeroAllocated?: boolean } | null;
      setHideZeroAllocated(settings?.hideZeroAllocated || false);

      const { data: clustersData } = await supabase
        .from("poll_clusters")
        .select("*")
        .eq("poll_id", poll.id)
        .order("display_order");

      if (clustersData) {
        const clustersWithSubs = await Promise.all(
          clustersData.map(async (cluster) => {
            const { data: subs } = await supabase
              .from("poll_sub_categories")
              .select("*")
              .eq("cluster_id", cluster.id)
              .order("display_order");

            return {
              id: cluster.id,
              label: cluster.label,
              description: cluster.description || "",
              image_url: cluster.image_url || undefined,
              order: cluster.display_order,
              subCategories: subs?.map(sub => ({
                id: sub.id,
                label: sub.label,
                description: sub.description || "",
                order: sub.display_order
              })) || []
            };
          })
        );
        setClusters(clustersWithSubs);

        // Initialize even split
        const evenSplit = Math.floor(100 / clustersWithSubs.length);
        const remainder = 100 - (evenSplit * clustersWithSubs.length);
        const initial: Record<string, number> = {};
        clustersWithSubs.forEach((cluster, index) => {
          initial[cluster.id] = evenSplit + (index === 0 ? remainder : 0);
        });
        setClusterAllocations(initial);
      }

      setLoading(false);

      // Check if user/session has already submitted
      const sessionKey = localStorage.getItem("sessionKey");
      const { data: { user } } = await supabase.auth.getUser();

      const { data: existingResponse } = await supabase
        .from("poll_responses")
        .select("id")
        .eq("poll_id", poll.id)
        .or(`user_id.eq.${user?.id || 'null'},session_key.eq.${sessionKey || 'null'}`)
        .maybeSingle();

      if (existingResponse) {
        // Load their existing allocations
        const { data: allocations } = await supabase
          .from("poll_allocations")
          .select("*")
          .eq("response_id", existingResponse.id);
        
        if (allocations) {
          const clusterAlloc: Record<string, number> = {};
          const subAlloc: Record<string, number> = {};
          
          allocations.forEach(alloc => {
            if (alloc.sub_category_id) {
              subAlloc[alloc.sub_category_id] = alloc.points;
            } else {
              clusterAlloc[alloc.cluster_id] = alloc.points;
            }
          });
          
          setClusterAllocations(clusterAlloc);
          setSubAllocations(subAlloc);
          setHasSubmitted(true);
          setShowResults(true);
        }
      }
    } catch (error) {
      console.error("Error loading poll:", error);
      setLoading(false);
    }
  };

  const handleClusterChange = (clusterId: string, value: number) => {
    setClusterAllocations(prev => ({ ...prev, [clusterId]: value }));
  };

  const handleSubChange = (subId: string, value: number) => {
    setSubAllocations(prev => ({ ...prev, [subId]: value }));
  };

  const handleSubmit = async () => {
    if (!pollId) return;
    if (totalPoints !== 100) {
      toast.error("Please allocate exactly 100 points");
      return;
    }

    setSubmitting(true);
    try {
      console.log("📊 Submitting poll response:", { 
        pollId,
        clusterAllocs: clusterAllocations, 
        subAllocs: subAllocations 
      });

      const sessionKey = localStorage.getItem("sessionKey") || crypto.randomUUID();
      localStorage.setItem("sessionKey", sessionKey);

      const { data: response, error: responseError } = await supabase
        .from("poll_responses")
        .insert({
          poll_id: pollId,
          session_key: sessionKey
        })
        .select()
        .single();

      if (responseError) {
        console.error("❌ Response insert error:", responseError);
        throw responseError;
      }

      console.log("✅ Response created:", response);

      const allocations = [];
      
      // Cluster allocations
      for (const [clusterId, points] of Object.entries(clusterAllocations)) {
        allocations.push({
          response_id: response.id,
          cluster_id: clusterId,
          sub_category_id: null,
          points
        });
      }

      // Sub-category allocations
      for (const [subId, points] of Object.entries(subAllocations)) {
        const cluster = clusters.find(c => c.subCategories.some(s => s.id === subId));
        if (cluster) {
          allocations.push({
            response_id: response.id,
            cluster_id: cluster.id,
            sub_category_id: subId,
            points
          });
        }
      }

      const { error: allocError } = await supabase
        .from("poll_allocations")
        .insert(allocations);

      if (allocError) {
        console.error("❌ Allocation insert error:", allocError);
        throw allocError;
      }

      console.log("✅ Allocations saved:", allocations.length, "records");

      setHasSubmitted(true);
      setShowResults(true);
      toast.success("Your response has been recorded!");
    } catch (error) {
      console.error("Error submitting poll:", error);
      toast.error("Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading poll...</div>;
  }

  if (!pollId || clusters.length === 0) {
    return null;
  }

  return (
    <div className="w-full my-6 border-t pt-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">
          {!showResults ? "Participate in Our Poll" : "Poll Results"}
        </h2>
      </div>
      {!showResults ? (
        <div className="space-y-4">
          <PollForm
            clusters={clusters}
            allocations={clusterAllocations}
            subAllocations={subAllocations}
            onClusterChange={handleClusterChange}
            onSubChange={handleSubChange}
            totalPoints={totalPoints}
          />
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || totalPoints !== 100}
            className="w-full"
          >
            {submitting ? "Submitting..." : "Submit Response"}
          </Button>
        </div>
      ) : (
        <PollResults
          pollId={pollId}
          clusters={clusters}
          userClusterAllocations={clusterAllocations}
          userSubAllocations={subAllocations}
          hasSubmitted={hasSubmitted}
        />
      )}
    </div>
  );
};
